/**
 * OutlinePanel - Modal overlay + Inline variant for outline → draft pipeline
 *
 * Three stages:
 * 1. Initial: Arc type selection, word count slider, "Generate Outline" button
 * 2. Editing: Editable section titles + notes, card references, per-section regenerate
 * 3. Drafting: Progress indicator while generating
 *
 * Exports:
 * - OutlinePanel: Modal wrapper (backward compat)
 * - OutlinePanelInline: Inline variant for workspace view mode
 * - OutlineState: Lifted state type for persistence across view switches
 *
 * @module @humanizer/studio/components/workspace/OutlinePanel
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import type { StackItem } from '../../contexts/BufferSyncContext';
import type { NoemaGraphResult, NoeticActType, NoeticActCategory, SicResult, ChapterGraphResult, NoemaStance } from '../../types/analysis';
import { createLogger } from '../../utils/debug';

const log = createLogger('OutlinePanel');

interface OutlineSection {
  title: string;
  cardRefs: number[];
  notes: string;
}

export interface DraftProvenance {
  type: 'verbatim' | 'paraphrase' | 'synthesis';
  sourceCardIndices: number[];
  startOffset: number;
  endOffset: number;
}

export interface DraftQualityReport {
  avgSicScore: number;
  avgManagerVoice: number;
  avgHedging: number;
  scaffoldRatio: number;
  totalSentences: number;
  warnings: string[];
}

export interface DraftMeta {
  arcType?: string;
  outlineSections?: Array<{ title: string; cardRefs: number[]; notes: string }>;
  generationModel?: string;
  humanizationModel?: string;
  humanized?: boolean;
  generationTimeMs?: number;
  humanizationTimeMs?: number;
  qualityReport?: DraftQualityReport | null;
  intentionalDrift?: {
    objectsLost: Array<{ label: string; salience: number }>;
    objectsAdded: Array<{ label: string }>;
    stanceShifts: Array<{ label: string; from: string; to: string }>;
    tensionPreserved: number;
    overallDrift: number;
  } | null;
  extractedInsights?: { concepts: string[]; phrases: string[]; hooksPlanted?: string[] } | null;
}

/** Lifted state for persisting outline across view switches */
export interface OutlineState {
  sections: OutlineSection[];
  arcType: ArcType;
  suggestedArc: string | null;
  userOverrodeArc: boolean;
  stage: Stage;
  targetWordCount: number;
  verbatimMode: boolean;
  humanize: boolean;
  humanizeIntensity: 'subtle' | 'moderate' | 'strong';
  humanizeInline: boolean;
  guidedMode: boolean;
  checkIntentionalDrift: boolean;
}

/** Book context for chapter-aware outline/draft generation */
export interface OutlineBookContext {
  bookTitle: string;
  bookDescription?: string;
  bookArcType?: string;
  chapterTitle: string;
  chapterTheme?: string;
  chapterSummary?: string;
  chapterPosition: number;
  totalChapters: number;
  siblingChapterTitles: string[];
  // Cross-chapter coherence (P0)
  priorConceptRegistry?: string[];
  priorPhraseCorpus?: string[];
  priorChapterSummaries?: Array<{ title: string; summary: string }>;
  chapterObligation?: {
    strongestObjection: string;
    howChapterAnswers: string;
    whatWouldBeLostIfRemoved: string;
  };
  // Source B: hooks planted by the immediately prior chapter
  priorChapterHooks?: string[];
}

interface OutlinePanelProps {
  stackItems: StackItem[];
  onClose: () => void;
  onDraftCreated: (text: string, sourceCards?: Array<{ title: string; excerpt: string }>, provenances?: DraftProvenance[], draftMeta?: DraftMeta) => void;
  bookContext?: OutlineBookContext;
}

interface OutlinePanelInlineProps {
  stackItems: StackItem[];
  initialState?: OutlineState | null;
  onStateChange?: (state: OutlineState) => void;
  onDraftCreated: (text: string, sourceCards?: Array<{ title: string; excerpt: string }>, provenances?: DraftProvenance[], draftMeta?: DraftMeta) => void;
  bookContext?: OutlineBookContext;
  /** Noema graph results from Tools pane, used to build intentionalBrief for draft generation */
  noemaGraphData?: NoemaGraphResult | null;
  /** SIC analysis results for subjective authenticity guidance in draft generation */
  sicData?: SicResult | null;
  /** Chapter graph results for cross-chapter thread continuity in draft generation */
  chapterGraphData?: ChapterGraphResult | null;
}

type Stage = 'initial' | 'editing' | 'drafting';
type ArcType = 'auto' | 'narrative' | 'thematic' | 'chronological' | 'exploratory';

const ARC_OPTIONS: Array<{ value: ArcType; label: string; description: string }> = [
  { value: 'auto', label: 'Auto', description: 'AI detects the best arc type based on your cards' },
  { value: 'narrative', label: 'Narrative', description: 'Story arc with beginning, development, climax, resolution' },
  { value: 'thematic', label: 'Thematic', description: 'Group by themes and analyze connections' },
  { value: 'chronological', label: 'Chronological', description: 'Order by time and trace evolution' },
  { value: 'exploratory', label: 'Exploratory', description: 'Follow curiosity, make unexpected connections' },
];

// ═══════════════════════════════════════════════════════════════════════════
// NOEMA → INTENTIONAL BRIEF MAPPING
// ═══════════════════════════════════════════════════════════════════════════

const ACT_CATEGORY_MAP: Record<string, NoeticActCategory> = {
  asserting: 'constitutive', defining: 'constitutive', establishing: 'constitutive', negating: 'constitutive',
  remembering: 'temporal', anticipating: 'temporal', retaining: 'temporal', protending: 'temporal',
  valuing: 'evaluative', regretting: 'evaluative', desiring: 'evaluative', fearing: 'evaluative',
  questioning: 'epistemic', doubting: 'epistemic', believing: 'epistemic', knowing: 'epistemic', imagining: 'epistemic',
  comparing: 'relational', distinguishing: 'relational', connecting: 'relational', opposing: 'relational',
};

function localActCategory(act: NoeticActType): NoeticActCategory {
  return ACT_CATEGORY_MAP[act] ?? 'constitutive';
}

/** Map NoemaGraphResult → intentionalBrief payload for generateDraft. */
function buildIntentionalBrief(graph: NoemaGraphResult): {
  coreObjects: Array<{ label: string; stance: string; salience: number }>;
  dominantCategories: Array<{ category: string; proportion: number }>;
  tensionTarget: number;
  stanceDirectives: string[];
} {
  const objects = graph.objects ?? [];
  const acts = graph.acts ?? [];
  const metrics = graph.metrics;

  // Top 8 objects by salience
  const coreObjects = [...objects]
    .sort((a, b) => b.salience - a.salience)
    .slice(0, 8)
    .map(o => ({ label: o.label, stance: o.stance, salience: o.salience }));

  // Act category distribution
  const catCounts: Record<string, number> = {};
  for (const act of acts) {
    const cat = localActCategory(act.actType);
    catCounts[cat] = (catCounts[cat] ?? 0) + 1;
  }
  const totalActs = acts.length || 1;
  const dominantCategories = Object.entries(catCounts)
    .map(([category, count]) => ({ category, proportion: count / totalActs }))
    .sort((a, b) => b.proportion - a.proportion)
    .slice(0, 5);

  // Stance directives from evolving/resisting objects
  const stanceDirectives = objects
    .filter(o => o.stance === 'evolving' || o.stance === 'resisting')
    .slice(0, 5)
    .map(o => `"${o.label}" has ${o.stance} stance — preserve this tension`);

  return {
    coreObjects,
    dominantCategories,
    tensionTarget: metrics?.tension ?? 0,
    stanceDirectives,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// SIC → DRAFT GUIDANCE MAPPING
// ═══════════════════════════════════════════════════════════════════════════

/** Map SicResult → sicGuidance payload for generateDraft. */
function buildSicGuidance(sic: SicResult): {
  sicScore: number;
  genre: string;
  directives: string[];
} {
  const directives: string[] = [];
  const f = sic.features;

  // Negative polarity: high score = bad
  if (f.meta_contamination && f.meta_contamination.score > 40) {
    directives.push('Eliminate structural narration, preambles, and "in conclusion" framing');
  }

  // Positive polarity: low score = lacking authenticity
  if (f.commitment_irreversibility && f.commitment_irreversibility.score < 30) {
    directives.push('Include concrete decisions with consequences — narrow options rather than listing alternatives');
  }
  if (f.anti_smoothing && f.anti_smoothing.score < 30) {
    directives.push('Avoid false balance — take asymmetric positions rather than presenting all sides equally');
  }
  if (f.scar_tissue_specificity && f.scar_tissue_specificity.score < 25) {
    directives.push('Include involuntary emotional residue and persistent reactions, not sanitized summaries');
  }
  if (f.situatedness_body_social && f.situatedness_body_social.score < 25) {
    directives.push('Ground in physical, social, or reputational specifics rather than abstract generalities');
  }
  if (f.bounded_viewpoint && f.bounded_viewpoint.score < 30) {
    directives.push('Write from a bounded perspective — acknowledge limits of knowledge rather than omniscient narration');
  }
  if (f.epistemic_risk_uncertainty && f.epistemic_risk_uncertainty.score < 30) {
    directives.push('Show genuine surprise and not-knowing, not performed caution or hedging');
  }
  if (f.time_pressure_tradeoffs && f.time_pressure_tradeoffs.score < 30) {
    directives.push('Reference urgency, deadlines, or asymmetric time awareness where the source material warrants it');
  }

  return {
    sicScore: sic.sicScore,
    genre: sic.genre,
    directives,
  };
}

/**
 * Build cross-chapter thread continuity brief from chapter graph analysis.
 * Provides through-line objects, transition data, position-aware tension,
 * and stance evolution directives for the current chapter.
 */
function buildChapterBrief(
  graph: ChapterGraphResult,
  chapterPosition: number,
): {
  throughLineObjects: Array<{ label: string; objectType: string; stance: string }>;
  transitionFromPrevious: {
    continuingObjects: string[];
    droppedObjects: string[];
    freshObjects: string[];
  } | null;
  positionTensionTarget: number;
  stanceEvolutionDirectives: string[];
  readerExpectations: string[];
} {
  // Through-line objects present in this chapter
  const throughLineObjects = graph.threads
    .filter(t => t.isThroughLine && t.presentIn.includes(chapterPosition))
    .slice(0, 6)
    .map(t => {
      // Find stance at current chapter position
      const stanceEntry = t.stanceTrajectory.find(s => s.chapterIndex === chapterPosition);
      return {
        label: t.label,
        objectType: t.objectType,
        stance: stanceEntry?.stance ?? 'neutral',
      };
    });

  // Transition from previous chapter
  const transition = graph.transitions.find(t => t.toChapter === chapterPosition) ?? null;
  const transitionFromPrevious = transition ? {
    continuingObjects: transition.continuingObjects,
    droppedObjects: transition.droppedObjects,
    freshObjects: transition.freshObjects,
  } : null;

  // Position-aware tension from the book-level tension arc
  const positionTensionTarget = graph.summary.tensionArc[chapterPosition] ?? 0;

  // Stance evolution directives: through-line objects evolving at this position
  const stanceEvolutionDirectives = graph.threads
    .filter(t => {
      const entry = t.stanceTrajectory.find(s => s.chapterIndex === chapterPosition);
      return entry?.stance === 'evolving';
    })
    .slice(0, 4)
    .map(t => `"${t.label}" is evolving at this chapter position — show the shift in perspective`);

  // Source A: derive reader expectations from chapter graph data
  const readerExpectations: string[] = [];

  // 1. Dropped objects with non-neutral stance in previous chapter → open question
  if (chapterPosition > 0) {
    const prevTransition = graph.transitions.find(t => t.toChapter === chapterPosition);
    if (prevTransition) {
      for (const dropped of prevTransition.droppedObjects) {
        const thread = graph.threads.find(t => t.label === dropped);
        if (thread) {
          const prevStance = thread.stanceTrajectory.find(s => s.chapterIndex === chapterPosition - 1);
          if (prevStance && prevStance.stance !== 'neutral') {
            readerExpectations.push(`"${dropped}" was ${prevStance.stance} in the previous chapter but dropped — reader may wonder what happened`);
          }
        }
      }
    }
  }

  // 2. Through-line objects with resisting or ambivalent stance → unresolved tension
  for (const thread of graph.threads) {
    if (!thread.isThroughLine) continue;
    const stanceEntry = thread.stanceTrajectory.find(s => s.chapterIndex === chapterPosition);
    if (stanceEntry && (stanceEntry.stance === 'resisting' || stanceEntry.stance === 'ambivalent')) {
      readerExpectations.push(`"${thread.label}" remains ${stanceEntry.stance} — reader carries unresolved tension about this`);
    }
  }

  // 3. Late-book (≥70% position) through-lines not yet affirming → reader expects resolution
  const totalChapters = graph.summary.totalChapters;
  if (totalChapters > 0 && chapterPosition / totalChapters >= 0.7) {
    for (const thread of graph.threads) {
      if (!thread.isThroughLine) continue;
      const stanceEntry = thread.stanceTrajectory.find(s => s.chapterIndex === chapterPosition);
      if (stanceEntry && stanceEntry.stance !== 'affirming') {
        const msg = `Late in the book: "${thread.label}" is still ${stanceEntry.stance} — reader expects resolution or deliberate open-endedness`;
        if (!readerExpectations.includes(msg)) readerExpectations.push(msg);
      }
    }
  }

  return {
    throughLineObjects,
    transitionFromPrevious,
    positionTensionTarget,
    stanceEvolutionDirectives,
    readerExpectations: readerExpectations.slice(0, 6),
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// SHARED OUTLINE LOGIC (used by both modal and inline)
// ═══════════════════════════════════════════════════════════════════════════

function useOutlineLogic(
  stackItems: StackItem[],
  onDraftCreated: OutlinePanelInlineProps['onDraftCreated'],
  initialState?: OutlineState | null,
  onStateChange?: (state: OutlineState) => void,
  bookContext?: OutlineBookContext,
  noemaGraphData?: NoemaGraphResult | null,
  sicData?: SicResult | null,
  chapterGraphData?: ChapterGraphResult | null,
) {
  const [stage, setStage] = useState<Stage>(initialState?.stage ?? 'initial');
  const [targetWordCount, setTargetWordCount] = useState(initialState?.targetWordCount ?? 2000);
  const [sections, setSections] = useState<OutlineSection[]>(initialState?.sections ?? []);
  const [arcType, setArcType] = useState<ArcType>(initialState?.arcType ?? 'auto');
  const [suggestedArc, setSuggestedArc] = useState<string | null>(initialState?.suggestedArc ?? null);
  const [userOverrodeArc, setUserOverrodeArc] = useState(initialState?.userOverrodeArc ?? false);
  const [verbatimMode, setVerbatimMode] = useState(initialState?.verbatimMode ?? false);
  const [humanize, setHumanize] = useState(initialState?.humanize ?? true);
  const [humanizeIntensity, setHumanizeIntensity] = useState<'subtle' | 'moderate' | 'strong'>(initialState?.humanizeIntensity ?? 'moderate');
  const [humanizeInline, setHumanizeInline] = useState(initialState?.humanizeInline ?? false);
  const [guidedMode, setGuidedMode] = useState(initialState?.guidedMode ?? false);
  const [checkIntentionalDrift, setCheckIntentionalDrift] = useState(initialState?.checkIntentionalDrift ?? false);
  const [regeneratingIndex, setRegeneratingIndex] = useState<number | null>(null);
  const [regeneratingAll, setRegeneratingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outlineMeta, setOutlineMeta] = useState<{ model?: string; generationTimeMs?: number } | null>(null);
  const [editingCardsIndex, setEditingCardsIndex] = useState<number | null>(null);
  const [addingSectionForm, setAddingSectionForm] = useState<{ title: string; notes: string; cardRefs: number[] } | null>(null);

  // ── Quality gate state (P1) ──
  type QualityScore = {
    compositeScore: number;
    tier: 'excellence' | 'polished' | 'raw_gem' | 'needs_refinement' | 'noise';
    dimensions: Record<string, number>;
    confidence: number;
  };
  const [qualityScores, setQualityScores] = useState<QualityScore[] | null>(null);
  const [scoringInProgress, setScoringInProgress] = useState(false);
  const [showQualityDetails, setShowQualityDetails] = useState(false);
  const scoredCardCountRef = useRef(0);

  // Auto-score cards when stage is 'initial' and cards are available
  useEffect(() => {
    if (stage !== 'initial' || stackItems.length === 0) return;
    if (!window.electronBuffer?.scoreStack) return;
    // Invalidate stale scores when card count changes
    if (scoredCardCountRef.current === stackItems.length && qualityScores) return;

    setScoringInProgress(true);
    setQualityScores(null);
    const cardTexts = stackItems.map(item => item.contentItem.text);
    window.electronBuffer.scoreStack({ cardTexts })
      .then(result => {
        setQualityScores(result.scores);
        scoredCardCountRef.current = stackItems.length;
      })
      .catch(err => {
        log.error('Quality scoring failed:', err);
      })
      .finally(() => {
        setScoringInProgress(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, stackItems.length]);

  const qualitySummary = useMemo(() => {
    if (!qualityScores) return null;
    const tierCounts: Record<string, number> = { excellence: 0, polished: 0, raw_gem: 0, needs_refinement: 0, noise: 0 };
    let totalScore = 0;
    for (const s of qualityScores) {
      tierCounts[s.tier] = (tierCounts[s.tier] ?? 0) + 1;
      totalScore += s.compositeScore;
    }
    const avgScore = Math.round(totalScore / qualityScores.length);
    const hasLowQuality = tierCounts.noise > 0 || tierCounts.needs_refinement > qualityScores.length / 2;
    return { tierCounts, avgScore, hasLowQuality, total: qualityScores.length };
  }, [qualityScores]);

  // Auto-seed outline for book chapters: when a chapter has a theme but no saved outline,
  // create a single section containing all cards and skip to the 'editing' stage
  useEffect(() => {
    if (bookContext?.chapterTheme && !initialState?.sections?.length && stackItems.length > 0 && stage === 'initial') {
      const allCardRefs = stackItems.map((_, i) => i);
      setSections([{
        title: bookContext.chapterTitle || 'Chapter Draft',
        cardRefs: allCardRefs,
        notes: bookContext.chapterTheme,
      }]);
      setStage('editing');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount only

  // Notify parent of state changes for persistence
  useEffect(() => {
    onStateChange?.({ sections, arcType, suggestedArc, userOverrodeArc, stage, targetWordCount, verbatimMode, humanize, humanizeIntensity, humanizeInline, guidedMode, checkIntentionalDrift });
  }, [sections, arcType, suggestedArc, userOverrodeArc, stage, targetWordCount, verbatimMode, humanize, humanizeIntensity, humanizeInline, guidedMode, checkIntentionalDrift, onStateChange]);

  const handleArcChange = useCallback((newArc: ArcType) => {
    setArcType(newArc);
    if (newArc !== 'auto') {
      setUserOverrodeArc(true);
    } else {
      setUserOverrodeArc(false);
    }
  }, []);

  const handleGenerateOutline = useCallback(async () => {
    if (!window.electronBuffer?.generateOutline) {
      setError('Outline generation requires the Electron app');
      return;
    }

    setStage('drafting');
    setError(null);

    try {
      const cards = stackItems.map(item => ({
        title: item.summary,
        excerpt: item.contentItem.text.slice(0, 500),
      }));

      // Pass 'auto' or undefined to let backend auto-detect
      const effectiveArc = arcType === 'auto' ? undefined : arcType;
      const outlineBookCtx = bookContext ? {
        bookTitle: bookContext.bookTitle,
        chapterTitle: bookContext.chapterTitle,
        chapterTheme: bookContext.chapterTheme,
        chapterSummary: bookContext.chapterSummary,
        siblingChapterTitles: bookContext.siblingChapterTitles,
        chapterPosition: bookContext.chapterPosition,
        totalChapters: bookContext.totalChapters,
        priorChapterSummaries: bookContext.priorChapterSummaries,
        priorConceptRegistry: bookContext.priorConceptRegistry,
        chapterObligation: bookContext.chapterObligation,
      } : undefined;
      const result = await window.electronBuffer.generateOutline(cards, effectiveArc, outlineBookCtx);
      setSections(result.sections);
      setOutlineMeta({ model: result.model, generationTimeMs: result.generationTimeMs });

      // Handle suggested arc from auto-detect
      if (result.suggestedArc && arcType === 'auto') {
        setSuggestedArc(result.suggestedArc);
      }

      setStage('editing');
    } catch (err) {
      log.error('Outline generation failed:', err);
      const detail = err instanceof Error ? err.message : String(err);
      setError(`Failed to generate outline: ${detail}`);
      setStage('initial');
    }
  }, [stackItems, arcType, bookContext]);

  const handleRegenerateOutline = useCallback(async () => {
    setStage('drafting');
    setError(null);

    try {
      const cards = stackItems.map(item => ({
        title: item.summary,
        excerpt: item.contentItem.text.slice(0, 500),
      }));

      const effectiveArc = arcType === 'auto' ? undefined : arcType;
      const outlineBookCtx = bookContext ? {
        bookTitle: bookContext.bookTitle,
        chapterTitle: bookContext.chapterTitle,
        chapterTheme: bookContext.chapterTheme,
        chapterSummary: bookContext.chapterSummary,
        siblingChapterTitles: bookContext.siblingChapterTitles,
        chapterPosition: bookContext.chapterPosition,
        totalChapters: bookContext.totalChapters,
        priorChapterSummaries: bookContext.priorChapterSummaries,
        priorConceptRegistry: bookContext.priorConceptRegistry,
        chapterObligation: bookContext.chapterObligation,
      } : undefined;
      const result = await window.electronBuffer!.generateOutline(cards, effectiveArc, outlineBookCtx);
      setSections(result.sections);
      setOutlineMeta({ model: result.model, generationTimeMs: result.generationTimeMs });

      if (result.suggestedArc && arcType === 'auto') {
        setSuggestedArc(result.suggestedArc);
      }

      setStage('editing');
    } catch (err) {
      log.error('Outline regeneration failed:', err);
      setError('Failed to regenerate outline.');
      setStage('editing');
    }
  }, [stackItems, arcType, bookContext]);

  const handleRegenerateSection = useCallback(async (index: number) => {
    if (!window.electronBuffer?.regenerateSection) return;
    const section = sections[index];
    if (!section) return;

    setRegeneratingIndex(index);
    setError(null);

    try {
      const cardTexts = stackItems.map(item => item.contentItem.text);
      const effectiveArc = arcType === 'auto' ? (suggestedArc ?? 'narrative') : arcType;
      const result = await window.electronBuffer.regenerateSection(index, section, cardTexts, effectiveArc);
      setSections(prev => prev.map((s, i) => i === index ? { ...s, title: result.title, notes: result.notes } : s));
    } catch (err) {
      log.error('Section regeneration failed:', err);
      setError(`Failed to regenerate section ${index + 1}.`);
    } finally {
      setRegeneratingIndex(null);
    }
  }, [sections, stackItems, arcType, suggestedArc]);

  const handleGenerateDraft = useCallback(async () => {
    if (!window.electronBuffer?.generateDraft) {
      setError('Draft generation requires the Electron app');
      return;
    }

    setStage('drafting');
    setError(null);

    try {
      const cardTexts = stackItems.map(item => item.contentItem.text);
      const effectiveArc = arcType === 'auto' ? (suggestedArc ?? 'narrative') : arcType;
      const intentionalBrief = noemaGraphData ? buildIntentionalBrief(noemaGraphData) : undefined;
      const sicGuidance = sicData ? buildSicGuidance(sicData) : undefined;
      if (sicGuidance) {
        log.info(`SIC guidance: ${sicGuidance.directives.length} directives, score ${sicGuidance.sicScore}/100`);
      }
      const chapterBrief = (chapterGraphData && bookContext)
        ? buildChapterBrief(chapterGraphData, bookContext.chapterPosition)
        : undefined;
      if (chapterBrief) {
        log.info(`Chapter brief: ${chapterBrief.throughLineObjects.length} through-lines, tension ${(chapterBrief.positionTensionTarget * 100).toFixed(0)}%`);
      }
      const result = await window.electronBuffer.generateDraft({
        sections,
        cardTexts,
        targetWordCount,
        verbatimMode: guidedMode ? false : verbatimMode,
        arcType: effectiveArc,
        humanize: guidedMode ? false : humanize,
        humanizeIntensity: humanize && !guidedMode ? humanizeIntensity : undefined,
        humanizeInline: humanize && !guidedMode ? humanizeInline : undefined,
        guidedMode,
        checkIntentionalDrift: checkIntentionalDrift && !guidedMode,
        bookContext,
        intentionalBrief,
        sicGuidance,
        chapterBrief,
      });

      const sourceCards = stackItems.map(item => ({
        title: item.summary,
        excerpt: item.contentItem.text.slice(0, 300),
      }));

      // Set stage to 'editing' BEFORE onDraftCreated — onDraftCreated switches
      // viewMode which unmounts this component, so setStage after would be lost
      setStage('editing');
      // Explicitly persist state before unmount (useEffect won't fire in time)
      onStateChange?.({ sections, arcType, suggestedArc, userOverrodeArc, stage: 'editing', targetWordCount, verbatimMode, humanize, humanizeIntensity, humanizeInline, guidedMode, checkIntentionalDrift });

      onDraftCreated(result.draft, sourceCards, result.provenances, {
        arcType: result.arcType ?? effectiveArc,
        outlineSections: result.outlineSections ?? sections,
        generationModel: result.generationModel,
        humanizationModel: result.humanizationModel,
        humanized: result.humanized,
        generationTimeMs: result.generationTimeMs,
        humanizationTimeMs: result.humanizationTimeMs,
        qualityReport: result.qualityReport ?? null,
        intentionalDrift: result.intentionalDrift ?? null,
        extractedInsights: result.extractedInsights ?? null,
      });
    } catch (err) {
      log.error('Draft generation failed:', err);
      const detail = err instanceof Error ? err.message : String(err);
      setError(`Failed to generate draft: ${detail}`);
      setStage('editing');
    }
  }, [stackItems, sections, targetWordCount, onDraftCreated, onStateChange, arcType, suggestedArc, userOverrodeArc, verbatimMode, humanize, humanizeIntensity, humanizeInline, guidedMode, checkIntentionalDrift, noemaGraphData, sicData, chapterGraphData, bookContext]);

  const updateSection = useCallback((index: number, updates: Partial<OutlineSection>) => {
    setSections(prev => prev.map((s, i) => i === index ? { ...s, ...updates } : s));
  }, []);

  const removeSection = useCallback((index: number) => {
    setSections(prev => prev.filter((_, i) => i !== index));
  }, []);

  const addSection = useCallback(() => {
    setAddingSectionForm({ title: '', notes: '', cardRefs: [] });
  }, []);

  const commitAddSection = useCallback(() => {
    if (!addingSectionForm || !addingSectionForm.title.trim()) return;
    setSections(prev => [...prev, {
      title: addingSectionForm.title.trim(),
      cardRefs: addingSectionForm.cardRefs,
      notes: addingSectionForm.notes,
    }]);
    setAddingSectionForm(null);
  }, [addingSectionForm]);

  const cancelAddSection = useCallback(() => {
    setAddingSectionForm(null);
  }, []);

  const handleRegenerateAll = useCallback(async () => {
    if (!window.electronBuffer?.regenerateSection || sections.length === 0) return;
    setRegeneratingAll(true);
    setError(null);

    const cardTexts = stackItems.map(item => item.contentItem.text);
    const effectiveArc = arcType === 'auto' ? (suggestedArc ?? 'narrative') : arcType;

    for (let i = 0; i < sections.length; i++) {
      setRegeneratingIndex(i);
      try {
        const result = await window.electronBuffer.regenerateSection(i, sections[i], cardTexts, effectiveArc);
        setSections(prev => prev.map((s, idx) => idx === i ? { ...s, title: result.title, notes: result.notes } : s));
      } catch (err) {
        log.error(`Regenerate all: section ${i} failed:`, err);
        setError(`Failed to regenerate section ${i + 1}.`);
        break;
      }
    }

    setRegeneratingIndex(null);
    setRegeneratingAll(false);
  }, [sections, stackItems, arcType, suggestedArc]);

  return {
    stage, sections, arcType, suggestedArc, userOverrodeArc, targetWordCount, verbatimMode,
    humanize, humanizeIntensity, humanizeInline, guidedMode, checkIntentionalDrift,
    regeneratingIndex, regeneratingAll, error,
    outlineMeta, editingCardsIndex, setEditingCardsIndex,
    addingSectionForm, setAddingSectionForm, commitAddSection, cancelAddSection,
    setTargetWordCount, setVerbatimMode, setHumanize, setHumanizeIntensity, setHumanizeInline, setGuidedMode, setCheckIntentionalDrift,
    handleArcChange,
    handleGenerateOutline, handleRegenerateOutline, handleRegenerateSection, handleRegenerateAll,
    handleGenerateDraft, updateSection, removeSection, addSection,
    noemaGraphData,
    sicData,
    qualityScores, scoringInProgress, showQualityDetails, setShowQualityDetails, qualitySummary,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// CARD USAGE SUMMARY (7C)
// ═══════════════════════════════════════════════════════════════════════════

function CardUsageSummary({ sections, stackItems }: {
  sections: OutlineSection[];
  stackItems: StackItem[];
}) {
  const cardUsageCount = new Map<number, number>();
  for (const section of sections) {
    for (const ref of section.cardRefs) {
      cardUsageCount.set(ref, (cardUsageCount.get(ref) ?? 0) + 1);
    }
  }

  const usedCount = cardUsageCount.size;
  const totalCount = stackItems.length;
  const unusedCards = stackItems
    .map((item, i) => ({ item, index: i }))
    .filter(({ index }) => !cardUsageCount.has(index));

  return (
    <div className="outline-panel__card-usage">
      <span className="outline-panel__card-usage-label">
        Card usage: {usedCount}/{totalCount} cards used
      </span>
      {unusedCards.length > 0 && (
        <div className="outline-panel__card-usage-unused">
          Unused: {unusedCards.map(({ item, index }) => (
            <span key={index} className="outline-panel__card-usage-chip">
              {index + 1}. {item.summary.slice(0, 25)}{item.summary.length > 25 ? '...' : ''}
            </span>
          ))}
        </div>
      )}
      {Array.from(cardUsageCount.entries())
        .filter(([, count]) => count > 1)
        .map(([cardIdx, count]) => (
          <span key={cardIdx} className="outline-panel__card-usage-multi">
            Card {cardIdx + 1} ({'\u00D7'}{count})
          </span>
        ))
      }
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// OUTLINE CONTENT (shared between modal and inline)
// ═══════════════════════════════════════════════════════════════════════════

function OutlineContent({ stackItems, logic }: {
  stackItems: StackItem[];
  logic: ReturnType<typeof useOutlineLogic>;
}) {
  const {
    stage, sections, arcType, suggestedArc, userOverrodeArc, targetWordCount, verbatimMode,
    humanize, humanizeIntensity, humanizeInline, guidedMode, checkIntentionalDrift,
    regeneratingIndex, regeneratingAll, error,
    outlineMeta, editingCardsIndex, setEditingCardsIndex,
    addingSectionForm, setAddingSectionForm, commitAddSection, cancelAddSection,
    setTargetWordCount, setVerbatimMode, setHumanize, setHumanizeIntensity, setHumanizeInline, setGuidedMode, setCheckIntentionalDrift,
    handleArcChange,
    handleGenerateOutline, handleRegenerateOutline, handleRegenerateSection, handleRegenerateAll,
    handleGenerateDraft, updateSection, removeSection, addSection,
    noemaGraphData: logicNoemaGraphData,
    sicData: logicSicData,
    qualityScores, scoringInProgress, showQualityDetails, setShowQualityDetails, qualitySummary,
  } = logic;

  // SIC context chip
  const sicChip = logicSicData ? {
    score: logicSicData.sicScore,
    directives: buildSicGuidance(logicSicData).directives.length,
  } : null;

  // Noema context chip
  const noemaChip = logicNoemaGraphData ? (() => {
    const objCount = logicNoemaGraphData.objects?.length ?? 0;
    const tension = logicNoemaGraphData.metrics?.tension ?? 0;
    return { objCount, tension };
  })() : null;

  // Display label for arc type
  const arcDisplayLabel = arcType === 'auto' && suggestedArc
    ? `${suggestedArc.charAt(0).toUpperCase() + suggestedArc.slice(1)} (suggested)`
    : ARC_OPTIONS.find(o => o.value === arcType)?.description ?? '';

  return (
    <>
      {error && (
        <div className="outline-panel__error" role="alert">
          {error}
        </div>
      )}

      {stage === 'initial' && (
        <div className="outline-panel__initial">
          <div className="outline-panel__actions">
            <button
              className="outline-panel__btn outline-panel__btn--primary"
              onClick={handleGenerateOutline}
            >
              Generate Outline from {stackItems.length} cards
              {qualitySummary && (
                <span className="outline-panel__quality-avg-badge">avg {qualitySummary.avgScore}</span>
              )}
            </button>
          </div>

          <p className="outline-panel__description">
            Generate a structured outline from your stack cards,
            then create a cohesive draft.
          </p>

          {/* Arc type dropdown */}
          <div className="outline-panel__arc-types">
            <h3 className="outline-panel__subtitle">Arc type</h3>
            <select
              className="outline-panel__arc-select"
              value={arcType}
              onChange={(e) => handleArcChange(e.target.value as ArcType)}
            >
              {ARC_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="outline-panel__arc-description">
              {arcDisplayLabel}
            </p>
          </div>

          <label className="outline-panel__checkbox-label">
            <input
              type="checkbox"
              checked={verbatimMode}
              onChange={(e) => setVerbatimMode(e.target.checked)}
            />
            <span>Verbatim mode</span>
            <span className="outline-panel__checkbox-hint">
              — include direct quotes with source attribution
            </span>
          </label>

          <label className="outline-panel__checkbox-label">
            <input
              type="checkbox"
              checked={humanize}
              onChange={(e) => setHumanize(e.target.checked)}
            />
            <span>Humanize draft</span>
            <span className="outline-panel__checkbox-hint">
              — rewrite to sound natural and human
            </span>
          </label>

          {humanize && (
            <div className="outline-panel__humanize-options">
              <select
                className="outline-panel__arc-select"
                value={humanizeIntensity}
                onChange={(e) => setHumanizeIntensity(e.target.value as 'subtle' | 'moderate' | 'strong')}
              >
                <option value="subtle">Subtle — minimal changes</option>
                <option value="moderate">Moderate — natural flow</option>
                <option value="strong">Strong — full rewrite</option>
              </select>
              <label className="outline-panel__checkbox-label outline-panel__checkbox-label--nested">
                <input
                  type="checkbox"
                  checked={humanizeInline}
                  onChange={(e) => setHumanizeInline(e.target.checked)}
                />
                <span>Single-pass mode</span>
                <span className="outline-panel__checkbox-hint">
                  — humanize during generation (faster, less control)
                </span>
              </label>
            </div>
          )}

          <label className="outline-panel__checkbox-label">
            <input
              type="checkbox"
              checked={guidedMode}
              onChange={(e) => setGuidedMode(e.target.checked)}
            />
            <span>Writing guide</span>
            <span className="outline-panel__checkbox-hint">
              — get paragraph-level briefs instead of generated prose
            </span>
          </label>

          <div className="outline-panel__slider-group">
            <label className="outline-panel__label" htmlFor="word-count-slider">
              Target word count: <strong>{targetWordCount.toLocaleString()}</strong>
            </label>
            <input
              id="word-count-slider"
              type="range"
              className="outline-panel__slider"
              min={500}
              max={5000}
              step={100}
              value={targetWordCount}
              onChange={(e) => setTargetWordCount(Number(e.target.value))}
            />
            <div className="outline-panel__slider-labels">
              <span>500</span>
              <span>5,000</span>
            </div>
          </div>

          {/* Quality gate summary (P1) */}
          {(scoringInProgress || qualitySummary) && (
            <div className="outline-panel__quality-summary">
              {scoringInProgress ? (
                <span className="outline-panel__quality-scoring">Scoring cards...</span>
              ) : qualitySummary && (
                <>
                  <div className="outline-panel__quality-bar" title={`Average score: ${qualitySummary.avgScore}`}>
                    {(['excellence', 'polished', 'raw_gem', 'needs_refinement', 'noise'] as const).map(tier => {
                      const count = qualitySummary.tierCounts[tier] ?? 0;
                      if (count === 0) return null;
                      const pct = (count / qualitySummary.total) * 100;
                      return (
                        <div
                          key={tier}
                          className={`outline-panel__quality-segment outline-panel__quality-segment--${tier}`}
                          style={{ width: `${pct}%` }}
                        />
                      );
                    })}
                  </div>
                  <div className="outline-panel__quality-labels">
                    {(['excellence', 'polished', 'raw_gem', 'needs_refinement', 'noise'] as const).map(tier => {
                      const count = qualitySummary.tierCounts[tier] ?? 0;
                      if (count === 0) return null;
                      const labels: Record<string, string> = {
                        excellence: 'Excellence', polished: 'Polished', raw_gem: 'Raw gem',
                        needs_refinement: 'Needs work', noise: 'Noise',
                      };
                      return (
                        <span key={tier} className={`outline-panel__quality-label outline-panel__quality-label--${tier}`}>
                          {labels[tier]} {count}
                        </span>
                      );
                    })}
                    <span className="outline-panel__quality-avg">avg {qualitySummary.avgScore}</span>
                  </div>
                  {qualitySummary.hasLowQuality && (
                    <p className="outline-panel__quality-hint">Some cards may reduce draft quality</p>
                  )}
                </>
              )}
            </div>
          )}

          <div className="outline-panel__cards-preview">
            <h3 className="outline-panel__subtitle">
              Cards in stack
              {qualityScores && (
                <button
                  className="outline-panel__toggle-details"
                  onClick={() => setShowQualityDetails(!showQualityDetails)}
                >
                  {showQualityDetails ? 'Hide scores' : 'Show scores'}
                </button>
              )}
            </h3>
            {stackItems.map((item, i) => {
              const score = qualityScores?.[i];
              const isLow = score && (score.tier === 'noise' || score.tier === 'needs_refinement');
              const tierSymbol: Record<string, string> = {
                excellence: '\u2605', polished: '\u25C6', raw_gem: '\u25C7\u2728',
                needs_refinement: '\u25C7', noise: '\u2717',
              };
              return (
                <div key={i} className={`outline-panel__card-preview${isLow ? ' outline-panel__card-preview--low' : ''}`}>
                  <span className="outline-panel__card-num">{i + 1}</span>
                  {showQualityDetails && score && (
                    <span className={`outline-panel__card-tier outline-panel__card-tier--${score.tier}`} title={score.tier}>
                      {tierSymbol[score.tier] ?? '?'}
                    </span>
                  )}
                  <span className="outline-panel__card-title">{item.summary}</span>
                  {showQualityDetails && score && (
                    <span className="outline-panel__card-score">{score.compositeScore}</span>
                  )}
                  <span className="outline-panel__card-words">{item.wordCount.toLocaleString()}w</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Noema context active indicator */}
      {noemaChip && (stage === 'editing' || stage === 'initial') && (
        <div className="outline-panel__noema-chip" title="Noema graph analysis will inform draft generation with phenomenological context">
          Noema context active ({noemaChip.objCount} objects, tension: {(noemaChip.tension * 100).toFixed(0)}%)
        </div>
      )}

      {/* SIC context active indicator */}
      {sicChip && (stage === 'editing' || stage === 'initial') && (
        <div className="outline-panel__noema-chip" title="SIC analysis will guide draft generation with subjective authenticity directives">
          SIC: {sicChip.score}/100 ({sicChip.directives} directives)
        </div>
      )}

      {stage === 'editing' && (
        <div className="outline-panel__editing">
          <div className="outline-panel__actions">
            <button
              className="outline-panel__btn outline-panel__btn--secondary"
              onClick={handleRegenerateOutline}
              disabled={regeneratingAll}
            >
              Regenerate
            </button>
            <button
              className="outline-panel__btn outline-panel__btn--secondary"
              onClick={handleRegenerateAll}
              disabled={regeneratingAll || regeneratingIndex !== null || sections.length === 0}
              title="Regenerate all sections sequentially"
            >
              {regeneratingAll
                ? `Regenerating ${(regeneratingIndex ?? 0) + 1} of ${sections.length}...`
                : 'Regenerate All'}
            </button>
            <button
              className="outline-panel__btn outline-panel__btn--primary"
              onClick={handleGenerateDraft}
              disabled={sections.length === 0 || regeneratingAll}
            >
              {guidedMode ? 'Generate Writing Guide' : 'Generate Draft'}
            </button>
          </div>

          {/* Outline generation metadata bar */}
          {outlineMeta && (outlineMeta.model || outlineMeta.generationTimeMs) && (
            <div className="outline-panel__meta-bar">
              {outlineMeta.model && (
                <span className="outline-panel__meta-model">{outlineMeta.model}</span>
              )}
              {outlineMeta.generationTimeMs != null && (
                <span className="outline-panel__meta-time">
                  Generated in {(outlineMeta.generationTimeMs / 1000).toFixed(1)}s
                </span>
              )}
            </div>
          )}

          {/* Show suggested arc when auto-detected */}
          {suggestedArc && arcType === 'auto' && (
            <div className="outline-panel__suggested-arc">
              Arc: <strong>{suggestedArc}</strong> <span className="outline-panel__suggested-tag">(suggested)</span>
            </div>
          )}

          {/* Arc type dropdown in editing stage too */}
          <div className="outline-panel__arc-types outline-panel__arc-types--compact">
            <select
              className="outline-panel__arc-select"
              value={arcType}
              onChange={(e) => handleArcChange(e.target.value as ArcType)}
            >
              {ARC_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Humanize controls in editing stage */}
          <div className="outline-panel__humanize-row">
            <label className="outline-panel__checkbox-label">
              <input
                type="checkbox"
                checked={humanize}
                onChange={(e) => setHumanize(e.target.checked)}
              />
              <span>Humanize</span>
            </label>
            {humanize && (
              <>
                <select
                  className="outline-panel__arc-select outline-panel__humanize-select"
                  value={humanizeIntensity}
                  onChange={(e) => setHumanizeIntensity(e.target.value as 'subtle' | 'moderate' | 'strong')}
                >
                  <option value="subtle">Subtle</option>
                  <option value="moderate">Moderate</option>
                  <option value="strong">Strong</option>
                </select>
                <label className="outline-panel__checkbox-label">
                  <input
                    type="checkbox"
                    checked={humanizeInline}
                    onChange={(e) => setHumanizeInline(e.target.checked)}
                  />
                  <span>Single-pass</span>
                </label>
              </>
            )}
          </div>

          {/* Verbatim + Writing Guide controls in editing stage */}
          <label className="outline-panel__checkbox-label">
            <input type="checkbox" checked={verbatimMode} onChange={(e) => setVerbatimMode(e.target.checked)} />
            <span>Verbatim mode</span>
          </label>
          <label className="outline-panel__checkbox-label">
            <input type="checkbox" checked={guidedMode} onChange={(e) => setGuidedMode(e.target.checked)} />
            <span>Writing guide</span>
          </label>
          {logicNoemaGraphData && (
            <label className="outline-panel__checkbox-label">
              <input type="checkbox" checked={checkIntentionalDrift} onChange={(e) => setCheckIntentionalDrift(e.target.checked)} />
              <span>Check intentional drift</span>
              <span className="outline-panel__checkbox-hint">
                — compare source vs. draft noema graphs (adds ~30s)
              </span>
            </label>
          )}

          {sections.map((section, i) => (
            <div key={i} className="outline-panel__section">
              <div className="outline-panel__section-header">
                <span className="outline-panel__section-num">{i + 1}</span>
                <input
                  className="outline-panel__section-title-input"
                  value={section.title}
                  onChange={(e) => updateSection(i, { title: e.target.value })}
                  placeholder="Section title"
                />
                <button
                  className="outline-panel__section-regen"
                  onClick={() => handleRegenerateSection(i)}
                  disabled={regeneratingIndex !== null}
                  title="Regenerate this section"
                  aria-label="Regenerate section"
                >
                  {regeneratingIndex === i ? '...' : '\u21BB'}
                </button>
                <button
                  className="outline-panel__section-remove"
                  onClick={() => removeSection(i)}
                  title="Remove section"
                  aria-label="Remove section"
                >
                  &times;
                </button>
              </div>

              <div className="outline-panel__section-refs">
                {section.cardRefs.map(ref => (
                  <span key={ref} className="outline-panel__ref-chip">
                    Card {ref + 1}: {stackItems[ref]?.summary ?? '?'}
                  </span>
                ))}
                <button
                  className="outline-panel__edit-cards-btn"
                  onClick={() => setEditingCardsIndex(editingCardsIndex === i ? null : i)}
                  title="Edit card associations"
                  aria-label="Edit cards"
                >
                  {editingCardsIndex === i ? 'Done' : 'Edit cards'}
                </button>
              </div>

              {/* Card picker for this section */}
              {editingCardsIndex === i && (
                <div className="outline-panel__card-picker">
                  {stackItems.map((item, cardIdx) => {
                    const isSelected = section.cardRefs.includes(cardIdx);
                    return (
                      <button
                        key={cardIdx}
                        className={`outline-panel__card-picker-pill${isSelected ? ' outline-panel__card-picker-pill--active' : ''}`}
                        onClick={() => {
                          const newRefs = isSelected
                            ? section.cardRefs.filter(r => r !== cardIdx)
                            : [...section.cardRefs, cardIdx].sort((a, b) => a - b);
                          updateSection(i, { cardRefs: newRefs });
                        }}
                        title={item.summary}
                      >
                        {cardIdx + 1}. {item.summary.slice(0, 30)}{item.summary.length > 30 ? '...' : ''}
                      </button>
                    );
                  })}
                </div>
              )}

              <textarea
                className="outline-panel__section-notes"
                value={section.notes}
                onChange={(e) => updateSection(i, { notes: e.target.value })}
                placeholder="Notes for this section (optional)"
                rows={2}
              />
            </div>
          ))}

          {/* Add Section: form or button */}
          {addingSectionForm ? (
            <div className="outline-panel__add-section-form">
              <input
                className="outline-panel__section-title-input"
                value={addingSectionForm.title}
                onChange={(e) => setAddingSectionForm({ ...addingSectionForm, title: e.target.value })}
                placeholder="Section title"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitAddSection();
                  if (e.key === 'Escape') cancelAddSection();
                }}
              />
              <textarea
                className="outline-panel__section-notes"
                value={addingSectionForm.notes}
                onChange={(e) => setAddingSectionForm({ ...addingSectionForm, notes: e.target.value })}
                placeholder="Notes (optional)"
                rows={2}
              />
              <div className="outline-panel__card-picker">
                {stackItems.map((item, cardIdx) => {
                  const isSelected = addingSectionForm.cardRefs.includes(cardIdx);
                  return (
                    <button
                      key={cardIdx}
                      className={`outline-panel__card-picker-pill${isSelected ? ' outline-panel__card-picker-pill--active' : ''}`}
                      onClick={() => {
                        const newRefs = isSelected
                          ? addingSectionForm.cardRefs.filter(r => r !== cardIdx)
                          : [...addingSectionForm.cardRefs, cardIdx].sort((a, b) => a - b);
                        setAddingSectionForm({ ...addingSectionForm, cardRefs: newRefs });
                      }}
                      title={item.summary}
                    >
                      {cardIdx + 1}. {item.summary.slice(0, 30)}{item.summary.length > 30 ? '...' : ''}
                    </button>
                  );
                })}
              </div>
              <div className="outline-panel__add-section-form-actions">
                <button
                  className="outline-panel__btn outline-panel__btn--primary"
                  onClick={commitAddSection}
                  disabled={!addingSectionForm.title.trim()}
                >
                  Add
                </button>
                <button
                  className="outline-panel__btn outline-panel__btn--secondary"
                  onClick={cancelAddSection}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              className="outline-panel__add-section"
              onClick={addSection}
            >
              + Add Section
            </button>
          )}

          {/* Card usage summary */}
          {sections.length > 0 && stackItems.length > 0 && (
            <CardUsageSummary sections={sections} stackItems={stackItems} />
          )}

          <div className="outline-panel__slider-group">
            <label className="outline-panel__label" htmlFor="word-count-slider-edit">
              Target word count: <strong>{targetWordCount.toLocaleString()}</strong>
            </label>
            <input
              id="word-count-slider-edit"
              type="range"
              className="outline-panel__slider"
              min={500}
              max={5000}
              step={100}
              value={targetWordCount}
              onChange={(e) => setTargetWordCount(Number(e.target.value))}
            />
          </div>
        </div>
      )}

      {stage === 'drafting' && (
        <div className="outline-panel__drafting" role="status" aria-live="polite">
          <div className="outline-panel__spinner" aria-hidden="true" />
          <p className="outline-panel__drafting-text">
            {sections.length > 0
              ? (humanize && !humanizeInline ? 'Writing and humanizing draft...' : 'Writing draft...')
              : 'Analyzing your cards...'}
          </p>
          {checkIntentionalDrift && sections.length > 0 && (
            <p className="outline-panel__drafting-hint">
              Intentional drift analysis will run after draft generation.
            </p>
          )}
          <p className="outline-panel__drafting-hint">
            This may take a moment depending on your model.
          </p>
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MODAL WRAPPER (backward compat)
// ═══════════════════════════════════════════════════════════════════════════

export function OutlinePanel({ stackItems, onClose, onDraftCreated, bookContext }: OutlinePanelProps) {
  const logic = useOutlineLogic(stackItems, onDraftCreated, undefined, undefined, bookContext);
  const modalRef = useRef<HTMLDivElement>(null);

  // Focus trap: keep Tab cycling within the modal
  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;

    const getFocusable = () => modal.querySelectorAll<HTMLElement>(
      'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
    );

    // Auto-focus first focusable element
    const elements = getFocusable();
    if (elements.length > 0) elements[0].focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    };

    modal.addEventListener('keydown', handleKeyDown);
    return () => modal.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const titleText = logic.stage === 'initial' ? 'Generate Draft'
    : logic.stage === 'editing' ? 'Edit Outline'
    : 'Generating...';

  return (
    <div className="outline-panel__overlay" onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }} role="presentation">
      <div
        ref={modalRef}
        className="outline-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="outline-panel-title"
      >
        <div className="outline-panel__header">
          <h2 id="outline-panel-title" className="outline-panel__title">
            {titleText}
          </h2>
          <button
            className="outline-panel__close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="outline-panel__body">
          <OutlineContent stackItems={stackItems} logic={logic} />
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// INLINE VARIANT (workspace view mode)
// ═══════════════════════════════════════════════════════════════════════════

export function OutlinePanelInline({ stackItems, initialState, onStateChange, onDraftCreated, bookContext, noemaGraphData, sicData, chapterGraphData }: OutlinePanelInlineProps) {
  const logic = useOutlineLogic(stackItems, onDraftCreated, initialState, onStateChange, bookContext, noemaGraphData, sicData, chapterGraphData);

  return (
    <div className="outline-panel outline-panel--inline">
      <div className="outline-panel__body">
        <OutlineContent stackItems={stackItems} logic={logic} />
      </div>
    </div>
  );
}
